// Refund Customer — Phase 1F (H2/H3)
//
// ⛔ LAYAR INI MENGELUARKAN UANG KE CUSTOMER. Yang salah nol bisa ditarik
// dari rekening orang. Karena itu tiap tombol di sini punya DUA syarat, dan
// keduanya datang dari backend — bukan dari tebakan layar:
//
//   1. `meta.permissions`  — apakah PERAN ini boleh sama sekali
//   2. `status` barisnya    — apakah TAHAP ini menerima aksi itu
//
// Menebak salah satunya dari nama peran akan menampilkan tombol yang backend
// tolak, dan pengguna menyalahkan dirinya sendiri untuk pagar yang bekerja
// benar.
//
// NOL KONSTANTA BISNIS. Delapan status, lima penyebab, dua metode bayar, dan
// AMBANG Rp5.000.000 semuanya dari GET /api/refunds/meta. Ambang itu yang
// paling berbahaya kalau disalin: kalau Owner mengubahnya, layar yang
// menyalinnya akan menggambar batas yang bukan batas sebenarnya.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { StatusBadge, toneFromMeta } from "../components/etams/StatusBadge";
import { BusyButton } from "../components/etams/BusyButton";
import PilihFA from "../components/etams/PilihFA";
import ProofUploader from "../components/etams/ProofUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Undo2, RefreshCw, AlertCircle, Plus, ShieldAlert, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../lib/format";
import {
  listRefunds, getRefund, createRefund, approveRefund, rejectRefund,
  prosesRefund, selesaikanRefund, getRefundMeta, apiErrorMessage,
} from "../lib/refundApi";

const LABEL_PAKET = { REGULER: "Reguler", EXPRESS: "Express", SUPER_EXPRESS: "Super Express" };

function badge(meta, code) {
  const s = (meta?.statuses || []).find(x => x.code === code);
  return {
    label: s?.label || code || "—",
    description: s?.description || "",
    tone: toneFromMeta(s?.tone),
  };
}

export default function Refunds() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  // Paginasi + cari + urut di SERVER. `urutSah` cermin `URUT_SAH` di
  // services/refund_service.py. Nama customer & nomor FA tetap BISA DICARI
  // (backend meresolusinya jadi id), cuma nol bisa jadi kunci pengurut.
  const [total, setTotal] = useState(0);
  const daftar = useDaftar(rows, {
    server: { total, urutSah: ["created_at", "refund_number", "status", "amount_idr", "cause"] },
    urutAwal: { kunci: "created_at", arah: "desc" },
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [detail, setDetail] = useState(null);
  const [tolakFor, setTolakFor] = useState(null);
  const [alasanTolak, setAlasanTolak] = useState("");
  const [prosesFor, setProsesFor] = useState(null);
  const [metode, setMetode] = useState("");
  const [selesaiFor, setSelesaiFor] = useState(null);
  const [bukti, setBukti] = useState("");
  const [buatOpen, setBuatOpen] = useState(false);
  const [form, setForm] = useState({});
  // Bentuk `{fa, wo}` milik PilihFA; `fa_id` di `form` tetap sumber kirimnya.
  // ⚠ Dikosongkan BERSAMA `form` — dua bentuk dari pilihan yang sama.
  const [faTerpilih, setFaTerpilih] = useState(null);

  const status = params.get("status") || "";
  const cause = params.get("cause") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  const { cari: kata, limit, offset, sort, arah } = daftar.paramServer;
  const bangunParams = useCallback((ovr = {}) => ({
    ...(status && { status }),
    ...(cause && { cause }),
    ...(kata && { cari: kata }),
    limit, offset, sort, arah, ...ovr,
  }), [status, cause, kata, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await listRefunds(bangunParams({ offset: off, limit: n }));
    return r.data;
  }, [bangunParams]);

  const muat = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [m, r] = await Promise.all([
        meta ? Promise.resolve({ data: meta }) : getRefundMeta(),
        listRefunds(bangunParams()),
      ]);
      if (!meta) setMeta(m.data);
      setRows(r.data?.items || []);
      setTotal(typeof r.data?.total === "number" ? r.data.total : (r.data?.items || []).length);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [bangunParams, meta]);

  useEffect(() => { muat(); }, [muat]);

  const izin = meta?.permissions || {};

  //: ⛔ Ambang DARI META, bukan angka di layar. Dipakai untuk memberi tahu
  //: pengaju SEBELUM ia menekan tombol ke mana pengajuannya akan pergi —
  //: bukan sebagai pengganti keputusan backend.
  const ambang = meta?.ambang_owner_idr;

  //: ⚠ `wajibOwner` dibaca DI DALAM useMemo, bukan di luarnya.
  //:
  //: Versi pertama menulisnya di luar sebagai
  //: `meta?.penyebab_wajib_owner || []`. Bentuk itu melahirkan LARIK BARU
  //: tiap render, jadi dependensi useMemo berubah tiap kali dan memonya nol
  //: pernah bekerja — ia menghitung ulang persis sesering tanpa useMemo.
  //:
  //: ⛔ Cacat itu NOL terlihat di sini: `yarn build` lokal cuma menulis
  //: "Compiled with warnings". Cloudflare Pages menyetel `CI=true`, yang
  //: mengubah peringatan react-hooks jadi GALAT — dan build produksi
  //: pertama gagal karenanya, 30 Agustus 2026.
  const tujuanApproval = useMemo(() => {
    const wajibOwner = meta?.penyebab_wajib_owner || [];
    const n = Number(form.amount_idr || 0);
    if (!form.cause || !ambang) return null;
    if (wajibOwner.includes(form.cause)) return "Owner";
    return n > ambang ? "Owner" : "Kepala Cabang";
  }, [form.cause, form.amount_idr, ambang, meta]);

  const aksi = async (fn, pesan) => {
    setBusy(true);
    try {
      await fn();
      toast.success(pesan);
      setDetail(null); setTolakFor(null); setProsesFor(null);
      setSelesaiFor(null); setBuatOpen(false);
      await muat();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const bukaDetail = async (id) => {
    try {
      const { data } = await getRefund(id);
      setDetail(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const menungguApproval = (s) =>
    s === "MENUNGGU_APPROVAL_KC" || s === "MENUNGGU_APPROVAL_OWNER";

  return (
    <div className="space-y-4" data-testid="page-refunds">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Undo2 size={20} /> Refund Customer
          </h1>
          <p className="text-sm text-muted-foreground">
            Pengembalian uang ke customer. <strong>Nominal di atas{" "}
            {ambang ? formatIDR(ambang) : "ambang"}</strong> naik ke Owner.
          </p>
        </div>
        <div className="flex gap-2">
          {izin.create && (
            <Button onClick={() => { setBuatOpen(true); setForm({}); setFaTerpilih(null); }}
                    data-testid="rf-new">
              <Plus size={16} /> Ajukan refund
            </Button>
          )}
          <Button variant="outline" onClick={muat} disabled={loading}
                  data-testid="rf-refresh">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            Muat ulang
          </Button>
        </div>
      </div>

      <Card className="p-3">
        <div className="flex flex-wrap gap-2">
          <select className="border rounded-md h-9 px-2 text-sm bg-background"
                  value={status} onChange={e => setParam("status", e.target.value)}
                  data-testid="rf-status-filter">
            <option value="">Semua status</option>
            {(meta?.statuses || []).map(s => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
          <select className="border rounded-md h-9 px-2 text-sm bg-background"
                  value={cause} onChange={e => setParam("cause", e.target.value)}
                  data-testid="rf-cause-filter">
            <option value="">Semua penyebab</option>
            {(meta?.causes || []).map(c => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>
      </Card>

      {err && (
        <Card className="p-4 flex gap-2 items-start text-destructive">
          <AlertCircle size={18} className="mt-0.5 shrink-0" /><span>{err}</span>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <div className="p-3 pb-0">
          <PalangDaftar daftar={daftar} placeholder="Cari nomor / FA / customer…" testId="rf-palang"
                        ekspor={{ nama: "refund", kolom: [
                          { kunci: "refund_number", judul: "Nomor" },
                          { kunci: "fa_number", judul: "FA" },
                          { kunci: "customer_name", judul: "Customer" },
                          { kunci: "cause", judul: "Penyebab" },
                          { kunci: "amount_idr", judul: "Nominal (IDR)" },
                          { kunci: "status", judul: "Status" },
                          { kunci: "created_at", judul: "Diajukan", ambil: (r) => formatDate(r.created_at) },
                        ], ambilHalaman }} />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <ThUrut daftar={daftar} kunci="refund_number" className="p-2">Nomor</ThUrut>
              <th className="p-2">FA</th>
              <ThUrut daftar={daftar} kunci="customer_name" className="p-2">Customer</ThUrut>
              <th className="p-2">Penyebab</th>
              <ThUrut daftar={daftar} kunci="amount_idr" className="p-2 text-right" align="right">Nominal</ThUrut>
              <ThUrut daftar={daftar} kunci="status" className="p-2">Status</ThUrut>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                Memuat…</td></tr>
            )}
            {!loading && daftar.total === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">
                <Wallet size={28} className="mx-auto mb-2 opacity-40" />
                {daftar.cari.trim() ? "Tidak ada yang cocok dengan pencarian."
                  : status || cause
                  ? "Tidak ada refund yang cocok dengan penyaring ini."
                  : "Belum ada pengajuan refund."}
              </td></tr>
            )}
            {daftar.tampil.map(r => {
              const b = badge(meta, r.status);
              const c = (meta?.causes || []).find(x => x.code === r.cause);
              return (
                <tr key={r.id} className="border-t hover:bg-muted/30"
                    data-testid={`rf-row-${r.refund_number}`}>
                  <td className="p-2 font-medium">{r.refund_number}</td>
                  <td className="p-2">{r.fa_number || "—"}</td>
                  <td className="p-2">{r.customer_name || "—"}</td>
                  <td className="p-2" title={c?.description}>{c?.label || r.cause}</td>
                  <td className="p-2 text-right tabular-nums">
                    {formatIDR(r.amount_idr)}
                  </td>
                  <td className="p-2">
                    <StatusBadge tone={b.tone} title={b.description}>
                      {b.label}
                    </StatusBadge>
                  </td>
                  <td className="p-2 text-right">
                    <Button size="sm" variant="ghost"
                            onClick={() => bukaDetail(r.id)}
                            data-testid={`rf-detail-${r.refund_number}`}>
                      Rincian
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="rf-paginasi" />
      </Card>

      {/* ---------------- Rincian + aksi ---------------- */}
      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Refund {detail?.refund_number}</DialogTitle>
            <DialogDescription>
              {badge(meta, detail?.status).description}
            </DialogDescription>
          </DialogHeader>

          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-muted-foreground">FA</span><br />
                  {detail.fa_number || "—"}</div>
                <div><span className="text-muted-foreground">Customer</span><br />
                  {detail.customer_name || "—"}</div>
                <div><span className="text-muted-foreground">Nominal</span><br />
                  <strong className="text-base">{formatIDR(detail.amount_idr)}</strong></div>
                <div><span className="text-muted-foreground">Diajukan</span><br />
                  {formatDate(detail.requested_at)}</div>
              </div>

              <div>
                <span className="text-muted-foreground">Alasan pengajuan</span>
                <p>{detail.alasan}</p>
              </div>

              {/* ⛔ DASAR HITUNG WAJIB TAMPIL untuk refund keterlambatan.
                  Customer yang bertanya "kenapa segini" berhak dijawab
                  angkanya — bukan dengan "sistem yang menghitung". */}
              {detail.cause === "KETERLAMBATAN" && (
                <div className="rounded-md border p-2 bg-muted/30">
                  <span className="text-muted-foreground">Dasar hitung</span>
                  <p>Paket dibayar <strong>{LABEL_PAKET[detail.paket_dibayar] || detail.paket_dibayar}</strong> →
                    realisasi <strong>{LABEL_PAKET[detail.paket_realisasi] || detail.paket_realisasi}</strong>,{" "}
                    {detail.hari_kerja_realisasi} hari kerja.</p>
                </div>
              )}

              {detail.rejected_reason && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2">
                  <span className="text-muted-foreground">Alasan penolakan</span>
                  <p>{detail.rejected_reason}</p>
                </div>
              )}

              {/* Jejak persetujuan — DUA pelaku terpisah, karena refund besar
                  melewati KC LALU Owner. Satu baris gabungan akan menghapus
                  jejak salah satunya, dan justru itu yang dicari saat ada
                  sengketa. */}
              <div className="rounded-md border p-2">
                <span className="text-muted-foreground">Jejak persetujuan</span>
                <ul className="mt-1 space-y-0.5">
                  <li>Kepala Cabang:{" "}
                    {detail.kc_approved_at
                      ? formatDate(detail.kc_approved_at)
                      : <span className="text-muted-foreground">belum</span>}</li>
                  <li>Owner:{" "}
                    {detail.owner_approved_at
                      ? formatDate(detail.owner_approved_at)
                      : <span className="text-muted-foreground">belum</span>}</li>
                  <li>Dieksekusi:{" "}
                    {detail.processed_at
                      ? formatDate(detail.processed_at)
                      : <span className="text-muted-foreground">belum</span>}</li>
                </ul>
              </div>

              {detail.bukti_signed_url && (
                <a href={detail.bukti_signed_url} target="_blank" rel="noopener noreferrer"
                   className="text-primary underline">Lihat bukti transfer</a>
              )}
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {detail && menungguApproval(detail.status) && izin.approve && (
              <>
                <Button variant="destructive"
                        onClick={() => { setTolakFor(detail); setAlasanTolak(""); }}
                        data-testid="rf-reject-btn">Tolak</Button>
                <BusyButton busy={busy}
                            onClick={() => aksi(
                              () => approveRefund(detail.id), "Refund disetujui.")}
                            data-testid="rf-approve-btn">Setujui</BusyButton>
              </>
            )}
            {detail?.status === "DISETUJUI" && izin.execute && (
              <Button onClick={() => { setProsesFor(detail); setMetode(""); }}
                      data-testid="rf-proses-btn">Proses pembayaran</Button>
            )}
            {detail?.status === "DIPROSES" && izin.execute && (
              <Button onClick={() => { setSelesaiFor(detail); setBukti(""); }}
                      data-testid="rf-selesai-btn">Tandai selesai</Button>
            )}
            <Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Tolak ---------------- */}
      <Dialog open={!!tolakFor} onOpenChange={o => !o && setTolakFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tolak refund {tolakFor?.refund_number}?</DialogTitle>
            <DialogDescription>
              Customer berhak tahu kenapa uangnya tidak dikembalikan — alasannya
              minimal {meta?.min_alasan_tolak || 10} karakter.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={alasanTolak} onChange={e => setAlasanTolak(e.target.value)}
                    placeholder="Alasan penolakan" data-testid="rf-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setTolakFor(null)}>Urung</Button>
            <BusyButton variant="destructive" busy={busy}
                        disabled={alasanTolak.trim().length < (meta?.min_alasan_tolak || 10)}
                        onClick={() => aksi(
                          () => rejectRefund(tolakFor.id, { alasan: alasanTolak.trim() }),
                          "Refund ditolak.")}
                        data-testid="rf-reject-confirm">Tolak refund</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Proses ---------------- */}
      <Dialog open={!!prosesFor} onOpenChange={o => !o && setProsesFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Proses {prosesFor?.refund_number}</DialogTitle>
            <DialogDescription>
              Cara bayarnya ditentukan keadaan tagihan, bukan selera.
            </DialogDescription>
          </DialogHeader>
          <select className="w-full border rounded-md h-9 px-2 bg-background"
                  value={metode} onChange={e => setMetode(e.target.value)}
                  data-testid="rf-metode">
            <option value="">— pilih cara bayar —</option>
            {(meta?.metode_bayar || []).map(m => (
              <option key={m.code} value={m.code}>{m.label}</option>
            ))}
          </select>
          {metode && (
            <p className="text-xs text-muted-foreground">
              {(meta?.metode_bayar || []).find(m => m.code === metode)?.description}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProsesFor(null)}>Urung</Button>
            <BusyButton busy={busy} disabled={!metode}
                        onClick={() => aksi(
                          () => prosesRefund(prosesFor.id, { metode }),
                          "Refund diproses.")}
                        data-testid="rf-proses-confirm">Proses</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Selesai ---------------- */}
      <Dialog open={!!selesaiFor} onOpenChange={o => !o && setSelesaiFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selesaikan {selesaiFor?.refund_number}</DialogTitle>
            <DialogDescription>
              {selesaiFor?.metode_bayar === "TRANSFER_BALIK"
                ? "Transfer balik WAJIB disertai bukti — uang keluar yang tidak bisa dibuktikan sampai ke customer jadi beban ETAMS saat ada sengketa."
                : "Dipotong dari pelunasan — tidak ada uang yang keluar dari rekening, jadi tidak ada bukti transfer yang perlu dilampirkan."}
            </DialogDescription>
          </DialogHeader>
          {/* ⛔ DULU KOTAK TEKS "URL bukti transfer" — isinya bisa teks apa pun,
              nol ada berkas, dan ia NOL PERNAH masuk `file_metadata` sehingga
              nol pernah ikut backup. Di modul yang kalimatnya sendiri menyebut
              berkas ini bukti saat sengketa.

              Diganti 5 September 2026 dengan `ProofUploader` — komponen yang
              SUDAH ADA untuk bukti bayar, lalu berhenti di situ (POLA A).
              `endpoint` menunjuk `/refunds/{id}/bukti`, endpoint TERSENDIRI
              yang dijaga `REFUND_EXECUTE_ROLES`; memakai endpoint pembayaran
              berarti melonggarkannya untuk Finance, dan itu wewenang yang
              keputusan Owner sendiri pisahkan. */}
          {selesaiFor?.metode_bayar === "TRANSFER_BALIK" && (
            <ProofUploader
              endpoint={`/refunds/${selesaiFor.id}/bukti`}
              testIdPrefix="rf-bukti"
              disabled={busy}
              onUploaded={(r) => setBukti(r.file_url)}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelesaiFor(null)}>Urung</Button>
            <BusyButton busy={busy}
                        disabled={selesaiFor?.metode_bayar === "TRANSFER_BALIK"
                                  && !bukti.trim()}
                        onClick={() => aksi(
                          () => selesaikanRefund(selesaiFor.id,
                            bukti.trim() ? { bukti_url: bukti.trim() } : {}),
                          "Refund selesai.")}
                        data-testid="rf-selesai-confirm">Tandai selesai</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------- Ajukan ---------------- */}
      <Dialog open={buatOpen} onOpenChange={o => !o && setBuatOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Ajukan refund</DialogTitle>
            <DialogDescription>
              Status awalnya DITENTUKAN sistem dari penyebab dan nominalnya —
              bukan dipilih pengaju.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            {/* ⛔ DULU KOTAK TEKS yang meminta UUID 36 karakter diketik tangan.
                Diganti 5 September 2026 dengan `PilihFA` — komponen yang SUDAH
                ADA sejak 2 September untuk cacat yang sama di Serah Terima
                Tahap, lalu berhenti di satu layar itu.

                `denganWo={false}`: refund nol peduli WO. */}
            <div>
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                Pesanan (FA) yang direfund
              </span>
              <PilihFA
                value={faTerpilih}
                denganWo={false}
                testId="rf-pilih-fa"
                onChange={(v) => {
                  setFaTerpilih(v);
                  setForm(f => ({ ...f, fa_id: v?.fa?.id || null }));
                }}
              />
            </div>
            <select className="w-full border rounded-md h-9 px-2 bg-background"
                    value={form.cause || ""}
                    onChange={e => setForm(f => ({ ...f, cause: e.target.value }))}
                    data-testid="rf-cause">
              <option value="">— penyebab —</option>
              {(meta?.causes || []).map(c => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            {form.cause && (
              <p className="text-xs text-muted-foreground">
                {(meta?.causes || []).find(c => c.code === form.cause)?.description}
              </p>
            )}
            <Input type="number" placeholder="Nominal (Rp)"
                   value={form.amount_idr || ""}
                   onChange={e => setForm(f => ({ ...f, amount_idr: e.target.value }))}
                   data-testid="rf-amount" />

            {/* ⚠ Memberi tahu SEBELUM tombol ditekan ke mana pengajuan ini
                pergi. Bukan pengganti keputusan backend — cerminannya. */}
            {tujuanApproval && (
              <p className="text-xs flex items-center gap-1 text-muted-foreground">
                <ShieldAlert size={14} />
                Akan menunggu persetujuan <strong>{tujuanApproval}</strong>.
              </p>
            )}

            {form.cause === "KETERLAMBATAN" && (
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Paket dibayar" value={form.paket_dibayar || ""}
                       onChange={e => setForm(f => ({ ...f, paket_dibayar: e.target.value }))} />
                <Input placeholder="Paket realisasi" value={form.paket_realisasi || ""}
                       onChange={e => setForm(f => ({ ...f, paket_realisasi: e.target.value }))} />
                <Input type="number" placeholder="Hari kerja"
                       value={form.hari_kerja_realisasi || ""}
                       onChange={e => setForm(f => ({ ...f, hari_kerja_realisasi: e.target.value }))} />
              </div>
            )}

            <Textarea placeholder="Alasan pengajuan" value={form.alasan || ""}
                      onChange={e => setForm(f => ({ ...f, alasan: e.target.value }))}
                      data-testid="rf-alasan" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBuatOpen(false)}>Urung</Button>
            <BusyButton busy={busy}
                        disabled={!form.fa_id || !form.cause
                                  || !(Number(form.amount_idr) > 0)
                                  || (form.alasan || "").trim().length < (meta?.min_alasan || 3)}
                        onClick={() => aksi(() => createRefund({
                          fa_id: form.fa_id,
                          cause: form.cause,
                          amount_idr: Number(form.amount_idr),
                          alasan: (form.alasan || "").trim(),
                          paket_dibayar: form.paket_dibayar || null,
                          paket_realisasi: form.paket_realisasi || null,
                          hari_kerja_realisasi: form.hari_kerja_realisasi
                            ? Number(form.hari_kerja_realisasi) : null,
                        }), "Refund diajukan.")}
                        data-testid="rf-submit">Ajukan</BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
